import { gql } from '@apollo/client';

import { CountryCodes } from 'src/common/enums';
import { HierarchicalJobCategory as HierarchicalJobCategories } from 'src/modules/Profile/graphql/hierarchicalJobCategories';
import { HierarchicalLocation } from 'src/modules/Profile/graphql/hierarchicalLocations';

interface PageInfo {
  totalPage: number;
  totalCount: number;
}

interface Item {
  id: string;
  name: string;
}

interface CompanyData {
  Company: Item;
  activeJobCount: number;
}

interface CityData {
  City: Item;
  activeJobCount: number;
}

interface FreeQueryData {
  query: Item;
  activeJobCount: number;
}

interface JobRoleData {
  jobRole: Item;
}

interface CustomSlugPage {
  customSlugPage: Item & { slug: string };
}

interface GetCompaniesWithActiveJobs {
  companies: CompanyData[];
  pageInfo: PageInfo;
}

interface GetCitiesWithActiveJobs {
  cities: CityData[];
  pageInfo: PageInfo;
}

interface GetFreeQueriesWithActiveJobs {
  freeQueries: FreeQueryData[];
  pageInfo: PageInfo;
}

interface GetJobRolesWithActiveJobs {
  jobRoles: JobRoleData[];
  pageInfo: PageInfo;
}

interface GetCustomSlugPagesWithActiveJobs {
  customSlugPages: CustomSlugPage[];
  pageInfo: PageInfo;
}

interface GetDistrictsWithActiveJobs {
  locations: HierarchicalLocation[];
  pageInfo: PageInfo;
}

interface GetHierarchicalJobCategoriesWithActiveJobs {
  jobCategories: HierarchicalJobCategories[];
  pageInfo: PageInfo;
}

export interface GetCompaniesWithActiveJobsData {
  getCompaniesWithActiveJobs: GetCompaniesWithActiveJobs;
}

export interface GetCitiesWithActiveJobsData {
  getCitiesWithActiveJobs: GetCitiesWithActiveJobs;
}

export interface GetFreeQueriesWithActiveJobsData {
  getFreeQueriesWithActiveJobs: GetFreeQueriesWithActiveJobs;
}

export interface GetJobRolesWithActiveJobsData {
  getJobRolesWithActiveJobs: GetJobRolesWithActiveJobs;
}

export interface GetCustomSlugPagesWithActiveJobsData {
  getCustomSlugPagesWithActiveJobs: GetCustomSlugPagesWithActiveJobs;
}

export interface GetDistrictsWithActiveJobsData {
  getDistrictsWithActiveJobs: GetDistrictsWithActiveJobs;
}

export interface GetHierarchicalJobCategoriesWithActiveJobsData {
  getHierarchicalJobCategoriesWithActiveJobs: GetHierarchicalJobCategoriesWithActiveJobs;
}

export interface GetActiveJobsArg {
  countryCode?: CountryCodes;
  pageSize?: number;
  currentPage?: number;
  shouldGetAllData?: boolean;
}

export const queryGetCompaniesWithActiveJobs = gql`
  query getCompaniesWithActiveJobs(
    $countryCode: String
    $pageSize: Int
    $currentPage: Int
  ) {
    getCompaniesWithActiveJobs(
      countryCode: $countryCode
      pageSize: $pageSize
      currentPage: $currentPage
    ) {
      pageInfo {
        totalPage
      }
      companies {
        activeJobCount
        Company {
          id
          name
        }
      }
    }
  }
`;

// TODO(YAYO): This endpoint has been replaced query suggestedJobLocations
// on the mobile app. The new endpoint uses hierarchical locations.
export const queryGetCitiesWithActiveJobs = gql`
  query getCitiesWithActiveJobs(
    $countryCode: String
    $pageSize: Int
    $currentPage: Int
  ) {
    getCitiesWithActiveJobs(
      countryCode: $countryCode
      pageSize: $pageSize
      currentPage: $currentPage
    ) {
      pageInfo {
        totalPage
      }
      cities {
        activeJobCount
        City {
          id
          name
        }
      }
    }
  }
`;

export const queryFreeQueriesWithActiveJobs = gql`
  query getFreeQueriesWithActiveJobs(
    $countryCode: String
    $pageSize: Int
    $currentPage: Int
  ) {
    getFreeQueriesWithActiveJobs(
      countryCode: $countryCode
      pageSize: $pageSize
      currentPage: $currentPage
    ) {
      pageInfo {
        totalPage
      }
      freeQueries {
        query {
          id
          name: keyword
        }
      }
    }
  }
`;

export const queryJobRolesWithActiveJobs = gql`
  query getJobRolesWithActiveJobs(
    $countryCode: String
    $pageSize: Int
    $currentPage: Int
  ) {
    getJobRolesWithActiveJobs(
      countryCode: $countryCode
      pageSize: $pageSize
      currentPage: $currentPage
    ) {
      pageInfo {
        totalPage
      }
      jobRoles {
        jobRole {
          id
          name
        }
      }
    }
  }
`;

export const queryCustomSlugPagesWithActiveJobs = gql`
  query getCustomSlugPagesWithActiveJobs(
    $countryCode: String
    $pageSize: Int
    $currentPage: Int
  ) {
    getCustomSlugPagesWithActiveJobs(
      countryCode: $countryCode
      pageSize: $pageSize
      currentPage: $currentPage
    ) {
      pageInfo {
        totalPage
      }
      customSlugPages {
        customSlugPage {
          id
          slug
          name
        }
      }
    }
  }
`;

export const queryDistrictsWithActiveJobs = gql`
  query getDistrictsWithActiveJobs(
    $countryCode: String
    $pageSize: Int
    $currentPage: Int
    $shouldGetAllData: Boolean
  ) {
    getDistrictsWithActiveJobs(
      countryCode: $countryCode
      pageSize: $pageSize
      currentPage: $currentPage
      shouldGetAllData: $shouldGetAllData
    ) {
      locations {
        id
        name
        formattedName
        level
        slug
        parents {
          id
          name
          formattedName
          level
          slug
        }
      }
      pageInfo {
        totalCount
      }
    }
  }
`;

export const queryHierarchicalJobCategoriesWithActiveJobs = gql`
  query getHierarchicalJobCategoriesWithActiveJobs(
    $countryCode: String
    $pageSize: Int
    $currentPage: Int
    $shouldGetAllData: Boolean
  ) {
    getHierarchicalJobCategoriesWithActiveJobs(
      countryCode: $countryCode
      pageSize: $pageSize
      currentPage: $currentPage
      shouldGetAllData: $shouldGetAllData
    ) {
      jobCategories {
        id
        name
        level
        slug
        parents {
          id
          name
          level
          slug
        }
      }
      pageInfo {
        totalCount
      }
    }
  }
`;
